import React, { useContext, useEffect, useState } from 'react'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import MicroModal from 'micromodal'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { useParams } from 'react-router-dom'
import { ColumnDef, ColumnFiltersState, SortingState } from '@tanstack/react-table' 
import { TeamContext } from '../../MenuTabs/MenuTabs'
import { MemberPresetsTableType, MemberPresetTableData } from './data/MemberPresetTableData'
import CreateLicensePreset from '../../../../../orgs/CreateLicensePreset'
import useMemberTable from '../context/MemberTableContext'
import CreateGroupPreset from '../../../../../orgs/CreateGroupPreset'
import MembersTableButton from '../Members/MembersTableButton'
import { modalCreateMemberPresetId, modalManageMemberPresetId } from './data/Utils'
import Button from '../../../../common/button/Button'

const MemberPresetsTable = ({ memberPresetsData, refetchPresets }) => {

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([])
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => MemberPresetTableData( memberPresetsData ) )
  const { setUseHeader, refetchMemberPresets } = useMemberTable()

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader(['all'])
  }, [])

  const columns = React.useMemo<ColumnDef<MemberPresetsTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'memberPresetID',
            accessorKey: 'memberPresetID',
            prettyName: 'Member Preset ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>MEMBER PRESET ID</span>,
          },
          {
            id: 'title',
            accessorKey: 'title',
            prettyName: 'Title',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>TITLE</span>,
          },
          {
            id: 'members',
            accessorKey: 'members',
            prettyName: 'Members',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>MEMBERS</span>,
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={ info.row.original } />
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const Actions = ({ info }) => {
    return (
      <div className='msync-table-action-column'>
        <CreateGroupPreset
          presetID={ info.memberPresetID }
          type="edit"
          presetTitle={ info.title }
          selectedMembers={ info.presetMembers }
          orgID={ teamId }
          afterEdit={() => {
            refetchPresets()
          }}
        />
        <MembersTableButton
          btnName='Manage'
          id={ modalManageMemberPresetId( info.memberPresetID ) }
          info={ info }
          teamType={'msynclicenses'}
        />
      </div>
    )
  }

  const exportHeaders = getExportHeaders('team-licenses')
  const { teamData } = useContext(TeamContext)

  return (
    <>
      <div className="msync-table__header">
        <CreateGroupPreset
          type="add"
          orgID={ teamId }
          afterEdit={() => {
            refetchPresets()
          }}
        />
        <Button
          onClick={ () => MicroModal.show(modalCreateMemberPresetId) }
        >
          Create
        </Button>
      </div>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Member presets'
        context='MemberPresets'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
        <></>
      </TanstackContent>
    </>
  )
}

export default MemberPresetsTable
